import React, { useState, useEffect } from 'react';
import { Stack, TextField, Button, Snackbar, Typography, Link } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import LoginHeader from '../layoutComponents/LoginHeader.js';
import { login } from '../../apis/authService.js';
import ForgotPassword from './ForgotPassword.js';
import CreateAccount from './CreateAccount.js';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../../contexts/NotificationContext.js';

function Login({ setIsAuthenticated, setUserRole, setUserId }) {
  const [user, setUser] = useState({ username: '', password: '' });
  const [open, setOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showCreateAccount, setCreateAccount] = useState(false);
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  useEffect(() => {
    // Check for cached user session on component mount
    const jwtToken = sessionStorage.getItem('jwt');
    const userRole = sessionStorage.getItem('userRole');
    const userId = sessionStorage.getItem('userId');

    if (jwtToken && userRole && userId) {
      // Restore authentication
      setIsAuthenticated(true);
      setUserRole(userRole);
      setUserId(userId);
      navigate('/reservations'); // Skip to the reservation page
    }
  }, [setIsAuthenticated, navigate, setUserRole, setUserId]);

  const handleChange = (event) => {
    setUser({ ...user, [event.target.name]: event.target.value });
  };

  const handleLogin = (event) => {
    event.preventDefault();
    setLoading(true);
    
    login(user)  // Use API function
      .then(({ jwtToken, role, id, activationStatus }) => {
        if (!activationStatus) {
          setErrorMessage('Your account is not activated.');
          setOpen(true);
          return;
        }

        sessionStorage.setItem('jwt', jwtToken);
        sessionStorage.setItem('userRole', role);
        sessionStorage.setItem('userId', id);

        setIsAuthenticated(true);
        setUserRole(role);
        setUserId(id);
        navigate('/reservations');
      })
      .catch((err) => {
        console.error(err);
        // If it's a rate limit error, show the notification popup
        if (err.isRateLimit) {
          showNotification(err.message, 'warning');
        } else {
          // For other errors, use the existing Snackbar
          setErrorMessage(err.message || 'Login failed: Check your credentials');
          setOpen(true);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div>
      <LoginHeader />
      {!showForgotPassword && !showCreateAccount ? (
        <>
          <form onSubmit={handleLogin}>
            <Stack spacing={2} alignItems="center" mt={8}>
              <Typography variant="h4" gutterBottom>Login</Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Please Enter Your Credentials:
              </Typography>
              <TextField
                name="username"
                label="Username"
                onChange={handleChange}
                required
              />
              <TextField
                type="password"
                name="password"
                label="Password"
                onChange={handleChange}
                required
              />
              <Button variant="outlined" color="primary" type="submit" disabled={loading}>
                {loading ? 'Logging in...' : 'Login'}
              </Button>

              <Link 
                component="button" 
                variant="body2" 
                onClick={() => setCreateAccount(true)}
                disabled={loading}
              >
                Create Account
              </Link>
              
              <Link 
                component="button" 
                variant="body2" 
                onClick={() => setShowForgotPassword(true)}
                disabled={loading}
              >
                Forgot Password?
              </Link>

              {loading && (
                <div style={{ marginTop: '1rem' }}>
                  <CircularProgress size={24} />
                </div>
              )}
            </Stack>
          </form>
          <Snackbar
            open={open}
            autoHideDuration={3000}
            onClose={() => setOpen(false)}
            message={errorMessage}
          />
        </>
      ) : showForgotPassword ? (
        <ForgotPassword onBackToLogin={() => setShowForgotPassword(false)} />
      ) : (
        <CreateAccount onBackToLogin={() => setCreateAccount(false)} />
      )}
    </div>
  );
}

export default Login;
